<script setup lang="ts">
import { computed, shallowRef } from 'vue'
import { useRouter } from 'vue-router'

import RouteCodeBadge from '@/components/transit/RouteCodeBadge.vue'
import { useScrollRestore } from '@/composables/useScrollRestore'
import { useTransitIndex } from '@/composables/useTransitIndex'
import type { BusRoute } from '@/types'
import { compareRouteCodes } from '@/utils/routeOrder'
import { matchesNormalized, normalizeForSearch } from '@/utils/textSearch'

import LookupScreenShell from './LookupScreenShell.vue'

const router = useRouter()
const { index, isPending } = useTransitIndex()
const query = shallowRef('')

/** Sorted once per dataset, not per keystroke. */
const routes = computed<BusRoute[]>(() => {
  const all = index.value ? [...index.value.routesByCode.values()] : []
  return all.sort((a, b) => compareRouteCodes(a.code, b.code))
})

/**
 * 164 rows render in full — no virtualisation. It would add a scroll container,
 * a measurement pass and a class of bugs to save 164 nodes. The stop tab is the
 * one with a real list-size problem.
 */
const results = computed(() => {
  const term = normalizeForSearch(query.value)
  if (!term) return routes.value
  return routes.value.filter(
    (route) =>
      matchesNormalized(normalizeForSearch(route.code), term) ||
      matchesNormalized(normalizeForSearch(route.name), term),
  )
})

/**
 * The list scrolls in its own pane, not in `AppScreen`'s `<main>`, so it needs
 * its own id. The query is restored with the offset: without it, back would
 * restore a position measured in a filtered list into an unfiltered one.
 */
const { containerRef } = useScrollRestore({
  id: 'lookup-routes',
  capture: () => query.value,
  restore: (saved) => {
    query.value = saved
  },
})

function openRoute(code: string) {
  // No `?stop=` — entered from Look-up, not from a stop.
  router.push({ name: 'route', params: { routeCode: code } })
}
</script>

<template>
  <LookupScreenShell v-model="query">
    <p v-if="isPending" class="p-4 text-xs text-muted-foreground">Loading network data…</p>

    <ul
      v-else-if="results.length > 0"
      ref="containerRef"
      class="min-h-0 flex-1 overflow-y-auto overscroll-contain"
    >
      <li v-for="route in results" :key="route.code">
        <button
          type="button"
          class="flex w-full items-center gap-3 border-b px-3 py-2.5 text-left transition-colors hover:bg-muted"
          @click="openRoute(route.code)"
        >
          <RouteCodeBadge :code="route.code" size="sm" />
          <span class="min-w-0 flex-1 text-sm">{{ route.name }}</span>
        </button>
      </li>
    </ul>

    <p v-else class="p-4 text-sm text-muted-foreground">No route matches “{{ query }}”.</p>
  </LookupScreenShell>
</template>
